import React, { useState } from 'react';
import { X, Send, Bug, Lightbulb, CheckCircle, MessageSquare } from 'lucide-react';
import { db, collection, addDoc, handleFirestoreError, OperationType } from '../src/firebase';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const FeedbackModal: React.FC<FeedbackModalProps> = ({ isOpen, onClose }) => {
  const [type, setType] = useState<'suggestion' | 'bug'>('suggestion');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const newItem = {
        type,
        message,
        email,
        timestamp: new Date().toISOString()
      };
      
      await addDoc(collection(db, 'feedback'), newItem);

      setSubmitted(true);
      setTimeout(() => {
        onClose();
        setSubmitted(false);
        setMessage('');
        setEmail('');
        setType('suggestion');
      }, 2500);
    } catch (error) {
      handleFirestoreError(error, OperationType.CREATE, 'feedback');
      alert("An error occurred. Please check your connection.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all scale-100 relative">
        <button 
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition p-1 bg-gray-100 rounded-full z-10"
        >
          <X size={20} />
        </button>

        {!submitted ? (
          <>
            <div className="bg-gradient-to-r from-gray-800 to-gray-900 p-6">
              <h3 className="text-white text-xl font-bold flex items-center">
                <MessageSquare size={24} className="mr-2 opacity-80" />
                Your Feedback Matters
              </h3>
              <p className="text-gray-300 text-sm mt-1">Help us improve Bihar Teacher Help</p>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6">
              <div className="flex space-x-2 mb-6 bg-gray-100 p-1 rounded-lg">
                <button
                  type="button"
                  onClick={() => setType('suggestion')}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-semibold transition-all flex items-center justify-center ${
                    type === 'suggestion' 
                      ? 'bg-white text-orange-600 shadow-sm' 
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Lightbulb size={16} className="mr-2" />
                  Suggestion
                </button>
                <button
                  type="button"
                  onClick={() => setType('bug')}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-semibold transition-all flex items-center justify-center ${
                    type === 'bug' 
                      ? 'bg-white text-red-600 shadow-sm' 
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Bug size={16} className="mr-2" />
                  Report Bug
                </button>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  required
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={type === 'suggestion' ? "What features would you like to see?" : "Describe the issue you faced..."}
                  className="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-xl text-gray-800 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 outline-none transition min-h-[120px] resize-none shadow-sm"
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email (Optional)
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="For follow-up questions"
                  className="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-xl text-gray-800 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 outline-none transition shadow-sm"
                />
              </div>

              <button 
                type="submit"
                disabled={isSubmitting}
                className={`w-full py-3 px-6 rounded-xl font-bold text-white flex items-center justify-center space-x-2 transition shadow-lg ${
                  isSubmitting 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 transform hover:-translate-y-0.5'
                }`}
              >
                {isSubmitting ? (
                  <span>Sending...</span>
                ) : (
                  <>
                    <Send size={18} />
                    <span>Submit Feedback</span>
                  </>
                )}
              </button>
            </form>
          </>
        ) : (
          <div className="p-12 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={32} className="text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Thank You!</h3>
            <p className="text-gray-600">Your feedback has been received. We appreciate your input in making this platform better.</p>
          </div>
        )}
      </div>
    </div>
  );
};